// bbpPairings engine wrapper — runs the FIDE-endorsed Dutch pairing engine as WebAssembly.
//
// Replaces the JaVaFo-on-CheerpJ path, which had to download an entire Java VM (tens of MB)
// before it could pair a round. This module is ~550 KB total and runs in milliseconds.
//
// The compiled artefacts (js/vendor/bbp-pairings.{mjs,wasm}) are produced by
// scripts/build-bbp-wasm.sh from the Apache-2.0 source vendored at vendor/bbpPairings.

/**
 * Exit codes returned by bbpPairings (see vendor/bbpPairings/README.txt). Unlike JaVaFo,
 * which we could only observe as "no output", these distinguish *why* pairing failed —
 * which is the difference between a useful message and "something went wrong".
 */
export const BbpExit = {
    OK: 0,
    NO_VALID_PAIRING: 1,
    UNEXPECTED_ERROR: 2,
    INVALID_REQUEST: 3,
    LIMIT_EXCEEDED: 4,
    FILE_ERROR: 5
};

/**
 * Human-readable, actionable explanation for a non-zero exit code. `stderr` is included
 * when the engine gave one, because for INVALID_REQUEST it names the offending TRF line.
 */
export function describeExit(code, stderr) {
    const detail = (stderr || '').trim();
    // The message is translated BEFORE the engine's stderr is appended: the detail is a
    // raw TRF line from bbpPairings and is never a dictionary key. Whole templates with
    // {placeholders}, never spliced sentences — see TeamEngine.text in js/core/team-engine.js,
    // which supplies the English fallback when the translator is not loaded (node:test).
    const say = (key, params) =>
        (typeof window !== 'undefined' && window.TeamEngine)
            ? window.TeamEngine.text(key, params)
            : String(key).replace(/\{(\w+)\}/g, (m, k) => (params && params[k] != null ? params[k] : m));
    const withDetail = msg => (detail ? `${msg}\n\n${detail}` : msg);
    switch (code) {
        case BbpExit.OK:
            return null;
        case BbpExit.NO_VALID_PAIRING:
            return withDetail(say(
                'No legal pairing exists for this round. Every possible pairing would break an ' +
                'absolute rule — usually because too few players are present, or the remaining ' +
                'players have all already played each other.'
            ));
        case BbpExit.INVALID_REQUEST:
            return withDetail(say(
                'The engine rejected the tournament data. This normally means a player is missing ' +
                'a starting number, two players share one, or a recorded score does not match the ' +
                'results entered.'
            ));
        case BbpExit.LIMIT_EXCEEDED:
            return withDetail(say('The tournament is too large for the pairing engine to handle.'));
        case BbpExit.FILE_ERROR:
            return withDetail(say('The pairing engine could not read the tournament data.'));
        case BbpExit.UNEXPECTED_ERROR:
        default:
            return withDetail(say('The pairing engine failed unexpectedly (code {code}).', { code }));
    }
}

// Resolved lazily so the 484 KB WASM is only fetched when a Swiss round is actually paired —
// spectators and Monrad/round-robin tournaments never download it.
let factoryPromise = null;

function loadFactory() {
    if (!factoryPromise) {
        factoryPromise = import('../vendor/bbp-pairings.mjs').then(m => m.default);
    }
    return factoryPromise;
}

/**
 * Warm the engine up without pairing anything. Call when a Swiss tournament is opened so the
 * download happens while the arbiter is still looking at the player list, rather than during
 * the second they are waiting for pairings.
 */
export async function preload() {
    try {
        await loadFactory();
        return true;
    } catch {
        return false;
    }
}

/**
 * Run the Dutch pairing engine over a TRF document.
 *
 * A fresh module instance is created per call. bbpPairings is a command-line program full of
 * static state that is not designed to have main() run twice, and a new instance is only a
 * few milliseconds. It also means the in-memory filesystem is discarded every time, so
 * nothing accumulates between rounds.
 *
 * @param {string} trfText  a complete TRF document (see js/services/pairing-trf.js)
 * @returns {Promise<{ exitCode: number, output: string, stderr: string }>}
 */
export async function runDutch(trfText) {
    const createModule = await loadFactory();

    const stderrChunks = [];
    const stdoutChunks = [];
    const Module = await createModule({
        noInitialRun: true,
        print: line => stdoutChunks.push(line),
        printErr: line => stderrChunks.push(line)
    });

    const INPUT = '/input.trf';
    const OUTPUT = '/output.txt';

    Module.FS.writeFile(INPUT, trfText);

    // Under Node, Emscripten's shell shim mirrors the program's exit status into
    // process.exitCode. bbpPairings exiting non-zero is a normal, fully-handled outcome here
    // (we return it below), so it must not silently condemn the host process — that would
    // otherwise fail an entire test run, or a future server-side caller. No-op in browsers.
    const host = typeof process !== 'undefined' ? process : null;
    const hostExitCode = host ? host.exitCode : undefined;

    let exitCode;
    try {
        exitCode = Module.callMain(['--dutch', INPUT, '-p', OUTPUT]);
    } catch (e) {
        // An explicit exit() surfaces as an ExitStatus exception rather than a return value.
        if (e && typeof e.status === 'number') exitCode = e.status;
        else throw e;
    } finally {
        if (host) host.exitCode = hostExitCode;
    }
    if (typeof exitCode !== 'number') exitCode = 0;

    let output = '';
    if (exitCode === BbpExit.OK) {
        try {
            output = Module.FS.readFile(OUTPUT, { encoding: 'utf8' });
        } catch (e) {
            // Exit code said success but nothing was written — treat as an engine failure
            // rather than silently returning zero pairings.
            return {
                exitCode: BbpExit.UNEXPECTED_ERROR,
                output: '',
                stderr: [...stderrChunks, `output file missing: ${e && e.message}`].join('\n')
            };
        }
    }

    return {
        exitCode,
        output,
        stderr: stderrChunks.join('\n'),
        stdout: stdoutChunks.join('\n')
    };
}

/**
 * Parse the engine's `-p` output: a count line followed by "<white> <black>" starting numbers,
 * where a black number of 0 means the white player receives a bye.
 *
 * @returns {Array<{ whiteNo: number, blackNo: number|null }>} blackNo null = bye
 */
export function parsePairingList(output) {
    const lines = (output || '').split('\n').map(l => l.trim()).filter(l => l.length > 0);
    if (lines.length === 0) return [];

    const declared = parseInt(lines[0], 10);
    const pairs = [];
    for (let i = 1; i < lines.length; i++) {
        const parts = lines[i].split(/\s+/);
        if (parts.length < 2) continue;
        const whiteNo = parseInt(parts[0], 10);
        const blackNo = parseInt(parts[1], 10);
        if (!Number.isFinite(whiteNo)) continue;
        pairs.push({ whiteNo, blackNo: blackNo === 0 ? null : blackNo });
    }

    // The count line is the engine's own statement of intent; a mismatch means we misread the
    // output, which must not be papered over into a half-paired round.
    if (Number.isFinite(declared) && declared !== pairs.length) {
        throw new Error(`bbpPairings declared ${declared} pairings but ${pairs.length} were parsed`);
    }
    return pairs;
}
